import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Clock, Plus, Award, X, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const ACTIVITY_TYPES = [
  { value: 'ground-setup', label: '🏟️ Ground Setup', description: 'Setting up boundary, stumps, pitch preparation' },
  { value: 'umpiring', label: '⚖️ Umpiring', description: 'Officiating matches' },
  { value: 'scoring', label: '📊 Scoring', description: 'Keeping score during matches' },
  { value: 'equipment', label: '🎒 Equipment Management', description: 'Managing, transporting, maintaining equipment' },
  { value: 'transport', label: '🚗 Transport', description: 'Driving players or equipment' },
  { value: 'food-drinks', label: '🍕 Food & Drinks', description: 'Organizing refreshments, catering' },
  { value: 'event-planning', label: '📋 Event Planning', description: 'Organizing events, tournaments, meetings' },
  { value: 'coaching', label: '🏏 Coaching/Training', description: 'Helping with practice sessions' },
  { value: 'admin-work', label: '💼 Admin Work', description: 'Paperwork, registrations, communications' },
  { value: 'fundraising', label: '💰 Fundraising', description: 'Sponsorship, donations, events' },
  { value: 'photography', label: '📸 Photography/Media', description: 'Taking photos, social media' },
  { value: 'other', label: '📝 Other', description: 'Other volunteer activities' }
];

const PlayerVolunteerSection = ({ playerId, playerName, clubId, games = [], events = [] }) => {
  const [volunteerLogs, setVolunteerLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    activity: '',
    description: '',
    hours: '',
    date: new Date().toISOString().split('T')[0],
    eventId: '',
    notes: ''
  });

  useEffect(() => {
    if (clubId && playerId) {
      loadMyVolunteerLogs();
    }
  }, [clubId, playerId]);

  const loadMyVolunteerLogs = async () => {
    try {
      setLoading(true);
      const logsRef = collection(db, 'clubs', clubId, 'volunteerLogs');
      const q = query(logsRef, where('playerId', '==', playerId), orderBy('date', 'desc'));
      const snapshot = await getDocs(q);

      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setVolunteerLogs(logs);
    } catch (error) {
      console.error('Error loading volunteer logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitHours = async (e) => {
    e.preventDefault();
    if (!formData.activity || !formData.hours || !formData.date) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const event = formData.eventId ?
        [...(games || []), ...(events || [])].find(e => e.id === formData.eventId) : null;

      const logData = {
        playerId: playerId,
        playerName: playerName,
        activity: formData.activity,
        activityLabel: ACTIVITY_TYPES.find(a => a.value === formData.activity)?.label || formData.activity,
        description: formData.description,
        hours: parseFloat(formData.hours),
        date: formData.date,
        eventId: formData.eventId || null,
        eventName: event?.name || null,
        status: 'pending', // Player submissions need approval
        submittedBy: playerId,
        createdAt: new Date().toISOString(),
        notes: formData.notes
      };

      await addDoc(collection(db, 'clubs', clubId, 'volunteerLogs'), logData);

      // Reset form
      setFormData({
        activity: '',
        description: '',
        hours: '',
        date: new Date().toISOString().split('T')[0],
        eventId: '',
        notes: ''
      });
      setShowAddForm(false);

      await loadMyVolunteerLogs();
      alert('Volunteer hours submitted for approval!');
    } catch (error) {
      console.error('Error submitting volunteer hours:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate stats
  const approvedLogs = volunteerLogs.filter(l => l.status === 'approved');
  const pendingLogs = volunteerLogs.filter(l => l.status === 'pending');
  const totalApprovedHours = approvedLogs.reduce((sum, l) => sum + (l.hours || 0), 0);
  const totalPendingHours = pendingLogs.reduce((sum, l) => sum + (l.hours || 0), 0);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header - Always visible */}
      <div
        className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900">My Volunteer Hours</h3>
            <p className="text-sm text-gray-500">
              {totalApprovedHours > 0 ? (
                <span className="text-green-600 font-semibold">{totalApprovedHours.toFixed(1)}h approved</span>
              ) : (
                'No volunteer hours yet'
              )}
              {totalPendingHours > 0 && (
                <span className="text-amber-600 ml-2">• {totalPendingHours.toFixed(1)}h pending</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAddForm(true);
            }}
            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus size={18} />
          </button>
          {expanded ? <ChevronUp className="text-gray-400" /> : <ChevronDown className="text-gray-400" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 sm:p-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{totalApprovedHours.toFixed(1)}</p>
              <p className="text-xs text-green-700">Approved Hours</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{pendingLogs.length}</p>
              <p className="text-xs text-amber-700">Pending</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{approvedLogs.length}</p>
              <p className="text-xs text-blue-700">Activities</p>
            </div>
          </div>

          {/* Log History */}
          {volunteerLogs.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No volunteer hours logged yet</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-2 text-green-600 hover:text-green-700 font-semibold text-sm"
              >
                + Log your first volunteer hours
              </button>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {volunteerLogs.slice(0, 10).map(log => (
                <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{log.activityLabel || log.activity}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${log.status === 'approved' ? 'bg-green-100 text-green-700' :
                          log.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                        }`}>
                        {log.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatDate(log.date)}
                      {log.eventName && <span> • {log.eventName}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold text-green-600">{log.hours}h</span>
                  </div>
                </div>
              ))}
              {volunteerLogs.length > 10 && (
                <p className="text-center text-sm text-gray-500 py-2">
                  + {volunteerLogs.length - 10} more entries
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Hours Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">Log Volunteer Hours</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitHours} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.activity}
                  onChange={(e) => setFormData(prev => ({ ...prev, activity: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Select Activity</option>
                  {ACTIVITY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                {formData.activity && (
                  <p className="text-xs text-gray-500 mt-1">
                    {ACTIVITY_TYPES.find(t => t.value === formData.activity)?.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hours <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={formData.hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 2.5"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Game/Event (optional)
                </label>
                <select
                  value={formData.eventId}
                  onChange={(e) => setFormData(prev => ({ ...prev, eventId: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">No specific event</option>
                  <optgroup label="Games">
                    {(games || []).map(g => (
                      <option key={g.id} value={g.id}>🏏 {g.name} ({formatDate(g.date)})</option>
                    ))}
                  </optgroup>
                  <optgroup label="Events">
                    {(events || []).map(e => (
                      <option key={e.id} value={e.id}>🎉 {e.name} ({formatDate(e.date)})</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Brief description of what you did"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  ⏳ Your submission will be reviewed by club admin before being approved.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold"
                >
                  {submitting ? 'Submitting...' : 'Submit Hours'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerVolunteerSection;